import { useQuery } from '@tanstack/react-query';
import axiosClient from './axiosClient';
// Import from runtimeHomeEnv directly so tests that mock axiosClient
// (only the default export) don't accidentally shadow setRuntimeHomeEnv.
import { setRuntimeHomeEnv } from './runtimeHomeEnv';

/**
 * Phase 3 — the deployment's runtime env + AUTHORITATIVE editability bit, served
 * by GET /api/app-env (never proxied — it must describe THIS pod's manifest).
 * `editable` is backend-driven (CURRENT_ENV === EDITABLE_ENV), not a build-time
 * REACT_APP_* compare, so flipping the editable env later is config-only.
 */
export interface AppEnvInfo {
  env: string;
  appEnvironment: string | null;
  ladderEnv: string; // dev01|qa|uat|integ|prod
  editableEnv: string;
  editable: boolean;
  availableEnvs: string[];
  remoteReadableEnvs: string[];
  /**
   * WAVE H — true iff the per-tenant editing-environment feature flag
   * (PER_TENANT_EDITING_ENV_ENABLED) is ON for this deployment. Optional so
   * the FE degrades gracefully against a backend that predates the field
   * (treated as `false` → legacy env-level editability everywhere).
   */
  perTenantEditingEnvEnabled?: boolean;
  /**
   * WAVE H — true iff GLOBAL catalogs are editable on THIS env. With the flag
   * ON this is `ladderEnv !== 'prod'` (globals editable on every non-prod env);
   * with the flag OFF (or field absent) it collapses to the legacy env-level
   * bit (`ladderEnv === editableEnv`, i.e. dev01-only).
   */
  globalsEditableHere?: boolean;
}

export function useAppEnv(): AppEnvInfo | undefined {
  const { data } = useQuery<AppEnvInfo>({
    queryKey: ['app-env'],
    queryFn: () =>
      axiosClient.get('/api/app-env').then((r) => {
        // Wire the runtime home env into the axios interceptor so every
        // subsequent request uses ladderEnv (the TRUE home) rather than the
        // build-time REACT_APP_EDITABLE_ENV fallback.
        const info = r.data as AppEnvInfo;
        if (info?.ladderEnv) setRuntimeHomeEnv(info.ladderEnv);
        return info;
      }),
    staleTime: Infinity, // pinned per deploy; never proxied
  });
  return data;
}
